import { useTranslation } from 'react-i18next';
import { ProviderIcon } from '../../../shared/ui/ProviderIcon.js';
import type { GatewayMethodOption } from '../gatewayMethodsApi.js';
import {
  CUSTOMER_PROVIDER_ICON_ASSET_BASE_PATH,
  customerPaymentProviderTitle,
  resolveCustomerGatewayProviderId,
} from '../paymentProvidersApi.js';
import { PaymentMethodOptionCard } from './PaymentMethodPicker.js';

export interface GatewayMethodsStepProps {
  readonly methods: readonly GatewayMethodOption[];
  readonly gatewayProviderSlotId?: string;
  readonly loading: boolean;
  readonly startingMethodId: string | null;
  readonly disabled: boolean;
  readonly onSelectMethod: (methodId: string) => void;
}

export function GatewayMethodsStep({
  methods,
  gatewayProviderSlotId,
  loading,
  startingMethodId,
  disabled,
  onSelectMethod,
}: GatewayMethodsStepProps): JSX.Element {
  const { t } = useTranslation('customer');
  const resolvedGatewayProviderId = resolveCustomerGatewayProviderId(gatewayProviderSlotId);
  const gatewayProviderTitle = customerPaymentProviderTitle(resolvedGatewayProviderId);

  return (
    <div className="flex flex-col gap-3" data-testid="customer-gateway-methods-step">
      <div>
        <p className="text-base font-semibold text-[var(--color-on-surface)]">
          {t('payment.gatewayMethods.title')}
        </p>
        <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">
          {t('payment.gatewayMethods.intro')}
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-[var(--color-on-surface-muted)]">
          {t('payment.gatewayMethods.loading')}
        </p>
      ) : null}

      {!loading && methods.length > 0 ? (
        <div className="flex flex-col gap-3" role="list">
          {methods.map((method) => {
            const isStarting = startingMethodId === method.id;
            const methodDisabled = disabled || (startingMethodId !== null && !isStarting);
            return (
              <PaymentMethodOptionCard
                key={method.id}
                name="customer-gateway-method"
                value={method.id}
                testId={`customer-gateway-method-${method.id}`}
                selected={isStarting}
                disabled={methodDisabled}
                title={isStarting ? t('checkout.redirecting') : method.name}
                description={t('payment.methodOption.CARD.description')}
                icon={
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--color-surface-muted)]">
                    <ProviderIcon
                      providerId={resolvedGatewayProviderId}
                      size="sm"
                      assetBasePath={CUSTOMER_PROVIDER_ICON_ASSET_BASE_PATH}
                      title={gatewayProviderTitle}
                    />
                  </span>
                }
                onSelect={() => {
                  onSelectMethod(method.id);
                }}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
